import { useEffect, useMemo, useState } from "react";
import { CHAT_DRAWER_WIDTH } from "../components/Chat/Sidebar";

export const DATA_PROCESSES_WIDTH = 280;
export const ANALYSIS_TRACE_WIDTH = 392;
export const DATA_PROCESSES_MIN_WIDTH = 240;
export const DATA_PROCESSES_MAX_WIDTH = 720;
export const DESKTOP_LAYOUT_MIN_WIDTH = 900;
export const MIN_RESERVED_CHAT_WIDTH = 640;

export const getDataProcessesWidth = (
  hasAnalysisTrace = false,
  customWidth = null,
  viewportWidth = null,
) => {
  const defaultWidth = hasAnalysisTrace ? ANALYSIS_TRACE_WIDTH : DATA_PROCESSES_WIDTH;
  const numericWidth = Number(customWidth);
  const requestedWidth = Number.isFinite(numericWidth) && numericWidth > 0
    ? numericWidth
    : defaultWidth;
  const viewportMax = Number.isFinite(Number(viewportWidth)) && Number(viewportWidth) > 0
    ? Math.max(DATA_PROCESSES_MIN_WIDTH, Number(viewportWidth) - 320)
    : DATA_PROCESSES_MAX_WIDTH;
  const maxWidth = Math.min(DATA_PROCESSES_MAX_WIDTH, viewportMax);

  return Math.round(Math.min(Math.max(requestedWidth, DATA_PROCESSES_MIN_WIDTH), maxWidth));
};

export const getReservedPanelInsets = ({
  viewportWidth,
  isSidebarOpen,
  isDataProcessesOpen,
  hasAnalysisTrace = false,
  dataProcessesWidth = null,
}) => {
  const isDesktop = viewportWidth >= DESKTOP_LAYOUT_MIN_WIDTH;
  const rightPanelWidth = getDataProcessesWidth(hasAnalysisTrace, dataProcessesWidth, viewportWidth);
  const requestedLeft = isDesktop && isSidebarOpen ? CHAT_DRAWER_WIDTH : 0;
  const requestedRight = isDesktop && isDataProcessesOpen ? rightPanelWidth : 0;

  let contentLeft = 0;
  let contentRight = 0;

  if (requestedLeft > 0 && requestedRight > 0) {
    if (viewportWidth - requestedLeft - requestedRight >= MIN_RESERVED_CHAT_WIDTH) {
      contentLeft = requestedLeft;
      contentRight = requestedRight;
    }
  } else if (requestedLeft > 0) {
    if (viewportWidth - requestedLeft >= MIN_RESERVED_CHAT_WIDTH) {
      contentLeft = requestedLeft;
    }
  } else if (requestedRight > 0) {
    if (viewportWidth - requestedRight >= MIN_RESERVED_CHAT_WIDTH) {
      contentRight = requestedRight;
    }
  }

  return {
    viewportWidth,
    isDesktop,
    contentLeft,
    contentRight,
    rightPanelWidth,
    sidebarOffset: contentLeft - contentRight,
    centerOffset: (contentLeft - contentRight) / 2,
    leftPanelOverlays: requestedLeft > 0 && contentLeft === 0,
    rightPanelOverlays: requestedRight > 0 && contentRight === 0,
  };
};

export default function useChatLayout({
  isSidebarOpen,
  isDataProcessesOpen,
  hasAnalysisTrace = false,
  dataProcessesWidth = null,
}) {
  const [viewportWidth, setViewportWidth] = useState(() => {
    if (typeof window === "undefined") return DESKTOP_LAYOUT_MIN_WIDTH;
    return window.innerWidth;
  });

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleResize = () => setViewportWidth(window.innerWidth);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return useMemo(
    () =>
      getReservedPanelInsets({
        viewportWidth,
        isSidebarOpen,
        isDataProcessesOpen,
        hasAnalysisTrace,
        dataProcessesWidth,
      }),
    [dataProcessesWidth, hasAnalysisTrace, isDataProcessesOpen, isSidebarOpen, viewportWidth],
  );
}
